import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://yabatho-api.onrender.com/v2/api';

function saveToken(token) {
  localStorage.setItem('token', token);
}

function getToken() {
  return localStorage.getItem('token');
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

async function request(
  endpoint,
  method = 'GET',
  body = null,
  customHeaders = {}
) {
  try {
    const config = {
      url: endpoint,
      method,
      headers: customHeaders,
    };

    if (['POST', 'PUT', 'PATCH'].includes(method.toUpperCase()) && body) {
      config.data = body;
    }

    const response = await apiClient(config);

    return response.data;
  } catch (error) {
    let message = 'API request failed';
    let status = null;
    let data = null;

    if (error.response) {
      status = error.response.status;
      data = error.response.data;
      const validationMessages = Array.isArray(data?.errors)
        ? data.errors.map((item) => item?.message).filter(Boolean)
        : [];
      message =
        validationMessages.join(' ') ||
        data?.message ||
        data?.error ||
        message;
    } else if (error.message) {
      message = error.message;
    }

    const err = new Error(message);
    err.status = status;
    err.data = data;
    throw err;
  }
}

// ─────── AUTH ───────
export async function login(credentials) {
  const data = await request('/auth/login', 'POST', credentials);
  if (data.token?.token) {
    saveToken(data.token.token);
  } else {
    console.error('❌ Token missing in login response:', data);
  }
  return data;
}

// ─────── ADMIN: Restaurants ───────
export const createRestaurant = (formData) => {
  const payload = {
    name: formData.name,
    description: formData.description,
    address: formData.address,
    phone: formData.phone,
    ownerEmail: formData.ownerEmail.trim().toLowerCase(),
    cuisineType: formData.cuisineType || formData.cuisine_type,
    isOpen: true,
    isPopular: false,
    topRated: false,
    openingHours: formData.openingHours || formData.opening_hours,
    coordinates: {
      latitude: parseFloat(formData.coordinates.latitude),
      longitude: parseFloat(formData.coordinates.longitude),
    },
  };

  if (formData.imageUrl) {
    payload.imageUrl = formData.imageUrl;
  }

  return request('/admin/restaurants', 'POST', payload);
};

export const updateRestaurant = (id, data) =>
  request(`/admin/restaurants/${id}`, 'PUT', data);

export const fetchAdminRestaurants = () => request('/admin/restaurants', 'GET');

export const deleteRestaurant = (id) =>
  request(`/admin/restaurants/${id}`, 'DELETE');

export const getRestaurantById = (id) =>
  request(`/admin/restaurants/${id}`, 'GET');

export const assignOwnerToRestaurant = (id, ownerData) =>
  request(`/admin/restaurants/${id}/assign-owner`, 'PATCH');

// ─────── GET USER ───────
export const getUserByEmail = (email) =>
  request(`/users?email=${encodeURIComponent(email)}`, 'GET');

// ─────── ADMIN: Customers ───────
export const fetchCustomers = (params = {}) =>
  request(`/admin/customers${buildQuery(params)}`, 'GET');

export const toggleCustomerStatus = (id, isAccountActive) =>
  request(`/admin/customers/${id}/toggle-status`, 'PATCH', {
    isAccountActive,
  });

// ─────── ADMIN: Riders ───────
export const fetchRiders = () => request('/admin/riders', 'GET');

export const createRider = (data) => request('/admin/riders', 'POST', data);

export const updateRider = (id, data) => request(`/admin/riders/${id}`, 'PUT', data);

export const toggleStatus = (id, isAccountActive) =>
  request(`/admin/riders/${id}/toggle-status`, 'PATCH', {
    isAccountActive,
  });

// Approve / Reject / Reopen Rider
export const updateRiderApproval = (riderId, approve, approvalStatus) =>
  request(`/admin/riders/${riderId}/toggle-approval`, 'PATCH', {
    isApproved: approvalStatus ? approvalStatus === 'approved' : approve,
    approvalStatus: approvalStatus || (approve ? 'approved' : 'rejected'),
  });

// ─────── GENERAL: Restaurants ───────
export const fetchRestaurants = (queryParams = '') =>
  request(`/restaurants${queryParams ? '?' + queryParams : ''}`, 'GET');

export const listUsers = (role) =>
  request(`/auth/list-users?role=${role}`, 'GET');

// ─────── ORDERS ───────
export const fetchAllOrders = (params = {}) =>
  request(`/admin/orders${buildQuery(params)}`, 'GET');

export const fetchOrdersByStatus = (status, params = {}) =>
  fetchAllOrders({ ...params, status });

export const getOrderById = (id) => request(`/admin/orders/${id}`, 'GET');

// ─────── PAYOUT REQUESTS ───────
export const getPayoutRequests = () => request('/admin/payout-requests', 'GET');

export const getFilteredPayoutRequests = (status) =>
  request(`/admin/payout-requests?status=${status}`, 'GET');

export const approvePayoutRequest = (requestId) =>
  request(`/admin/payout-requests/${requestId}/approve`, 'PATCH');

export const rejectPayoutRequest = (requestId) =>
  request(`/admin/payout-requests/${requestId}/reject`, 'PATCH');

// Rider weekly payouts
export const getRiderPayouts = (status = '') =>
  request(`/admin/rider-payouts${status ? `?status=${status}` : ''}`, 'GET');

export const markRiderPayoutPaid = (payoutId) =>
  request(`/admin/rider-payouts/${payoutId}/mark-paid`, 'PATCH');

export const getRestaurantPayouts = (status = '') =>
  request(`/admin/restaurant-payouts${status ? `?status=${status}` : ''}`, 'GET');

export const markRestaurantPayoutPaid = (payoutId) =>
  request(`/admin/restaurant-payouts/${payoutId}/mark-paid`, 'PATCH');

export const getLiveEarnings = () => request('/admin/live-earnings', 'GET');

export const getRestaurantOfflineAlerts = () =>
  request('/admin/restaurant-offline-alerts', 'GET');

const buildQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, value);
    }
  });
  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
};

export const getDineInDeposits = (params = {}) =>
  request(`/admin/dine-in-deposits${buildQuery(params)}`, 'GET');

export const reconcileDineInDeposit = (orderId, data) =>
  request(`/admin/dine-in-deposits/${orderId}/reconcile`, 'PATCH', data);

// ─────── EXPORT ALL ───────
const api = {
  request,
  login,

  // Restaurants
  createRestaurant,
  updateRestaurant,
  fetchAdminRestaurants,
  deleteRestaurant,
  getRestaurantById,
  assignOwnerToRestaurant,

  // Customers
  fetchCustomers,
  toggleCustomerStatus,

  // Riders
  fetchRiders,
  createRider,
  updateRider,
  toggleStatus,
  updateRiderApproval, // ← added here

  // General
  fetchRestaurants,
  listUsers,
  getUserByEmail,

  // Orders
  fetchAllOrders,
  fetchOrdersByStatus,
  getOrderById,

  // Payouts
  getPayoutRequests,
  getFilteredPayoutRequests,
  approvePayoutRequest,
  rejectPayoutRequest,
  getRiderPayouts,
  markRiderPayoutPaid,
  getRestaurantPayouts,
  markRestaurantPayoutPaid,
  getLiveEarnings,
  getRestaurantOfflineAlerts,
  getDineInDeposits,
  reconcileDineInDeposit,
};

export default api;
